import React, { useMemo, useState } from "react";
import {
  Zap,
  Radio,
  Waves,
  ArrowRightLeft,
  Fingerprint,
  Activity,
  TriangleAlert,
  AlertCircle,
  Bluetooth,
  RefreshCcw,
  Save,
  ChevronRight,
  Antenna,
  Copy,
  Clock,
} from "lucide-react";
import { FEED_FILTERS, FEED_CAP } from "utils/liveFeed";
import { showToast } from "utils/toast";
import { useTranslation, Trans } from "react-i18next";
import {
  gStr,
  IMPACT_FS_G,
  severityForG,
  ImpactMeter,
  ImpactPulse,
  AxisBars,
  eventDetailRows,
  gateVerdictLabel,
  SnippetSparkline,
} from "./common/impactVisuals";
import { dateLocale } from "i18n";

const FOCUS =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E07A3A]/45 focus-visible:ring-offset-1 focus-visible:ring-offset-white";

// tone → icon container tint. Restrained: the accent is reserved for impacts
// (the data this device exists to capture); everything else stays neutral.
const TONE = {
  accent: { bg: "#FFF3ED", fg: "#E07A3A" },
  info: { bg: "#EFF6FF", fg: "#2563EB" },
  ok: { bg: "#F0FDF4", fg: "#15803D" },
  warn: { bg: "#FEF3E7", fg: "#B45309" },
  error: { bg: "#FEF2F2", fg: "#DC2626" },
  muted: { bg: "#F5F4F1", fg: "#9CA3AF" },
  default: { bg: "#F3F4F6", fg: "#6B7280" },
};

const KIND_ICON = {
  impact: Zap,
  broadcast: Radio,
  waveform: Waves,
  beacon: Antenna,
  state: ArrowRightLeft,
  session: Activity,
  button: Fingerprint,
  store: Save,
  clock: Clock,
  warning: TriangleAlert,
  error: AlertCircle,
  ble: Bluetooth,
  sync: RefreshCcw,
  generic: Activity,
};

const timeOf = (ts) =>
  new Date(ts).toLocaleTimeString(dateLocale("en-GB"), { hour12: false });

// Expanded impact/broadcast row: the serial terminal's event tooltip in the
// Live tab's light theme — severity meter, rise/decay pulse, X/Y/Z bars, and
// the same label/value breakdown, inline under the row instead of on hover.
const ImpactDetail = ({ ev, txEv }) => {
  const { t } = useTranslation();
  const isImpact = ev.t === "impact";
  const isLoad = ev.t === "load";
  // The waveform pair: the load capture (stored full-res on the device) and
  // its 0x11 broadcast (decimated 38 samples, the only copy we can graph
  // live). A merged row carries the load as `ev` and the broadcast as `txEv`.
  const tx = txEv || (ev.t === "tx" && ev.pkt === "0x11" ? ev : null);
  const isWave = isLoad || !!tx;
  const load = isLoad ? ev : null;
  const [waveTab, setWaveTab] = useState(tx ? "sent" : "stored");

  const activeEv = isWave ? (waveTab === "sent" && tx ? tx : load || tx) : ev;
  const mg = isImpact
    ? ev.peak_mg
    : isWave
    ? activeEv?.max_mg
    : ev.pkt === "0x10"
    ? ev.mag_mg
    : ev.max_mg;
  const sev = severityForG(typeof mg === "number" ? mg / 1000 : null, false);
  const comps = [ev.x_mg, ev.y_mg, ev.z_mg];
  const hasAxes = !isWave && comps.some((c) => typeof c === "number");
  const rows = eventDetailRows(activeEv || ev);
  // Waveform events live on the 0–10 g low-band scale — the 0–60 g impact
  // severity meter would flatten them into nothing. They get the snippet
  // graph (Sent tab) and a range readout (Stored tab) instead.
  const showMeter = typeof mg === "number" && !isWave;
  const samples =
    isWave && waveTab === "sent" && Array.isArray(tx?.samples_mg)
      ? tx.samples_mg
      : null;
  const saturated = samples ? samples.some((s) => s >= 10000) : false;

  const copyJson = () => {
    navigator.clipboard?.writeText(JSON.stringify(activeEv || ev)).then(
      () => showToast.success(t("devices.impactVisual.copiedToClipboard")),
      () => showToast.error(t("devices.impactVisual.copyFailed"))
    );
  };

  return (
    <div className="border-t border-[#F7F6F4] bg-[#FBFAF9] px-4 py-3 pl-[92px] pr-6">
      {/* Sent vs Stored: the broadcast is decimated but graphable live; the
          stored copy is full-resolution but lives in flash (Export to graph). */}
      {isWave && load && tx && (
        <div className="mb-3 flex items-center gap-1">
          {[
            { id: "sent", label: t("devices.activityFeed.sentDecimated") },
            { id: "stored", label: t("devices.activityFeed.storedFullRes") },
          ].map((tabDef) => (
            <button
              key={tabDef.id}
              onClick={() => setWaveTab(tabDef.id)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition duration-150 ${FOCUS} ${
                waveTab === tabDef.id
                  ? "border-[#2563EB]/40 bg-[#EFF6FF] text-[#2563EB]"
                  : "border-transparent text-[#6B7280] hover:bg-[#F3F4F6]"
              }`}
            >
              {tabDef.label}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-x-10 gap-y-3 md:grid-cols-[minmax(0,360px)_minmax(200px,1fr)]">
        <div>
          {isWave && waveTab === "stored" && (load || !tx) && (
            <>
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[18px] font-bold tabular-nums leading-none text-[#2563EB]">
                  {gStr((load || ev).max_mg)}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                  {(load || ev).min_mg != null
                    ? t("devices.activityFeed.range", {
                        min: gStr((load || ev).min_mg),
                        max: gStr((load || ev).max_mg),
                      })
                    : t("devices.activityFeed.peak")}
                </span>
              </div>
              <p className="mt-2 rounded-lg border border-[#DDEBFF] bg-[#F5F9FF] px-3 py-2 text-[12px] leading-relaxed text-[#1E40AF]">
                <Trans
                  i18nKey="devices.activityFeed.storedNote"
                  components={{ bold: <span className="font-semibold" /> }}
                />
              </p>
            </>
          )}
          {samples && (
            <>
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-[18px] font-bold tabular-nums leading-none text-[#0D9488]">
                  {gStr(mg)}
                </span>
                {saturated ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[#DC2626]">
                    {t("devices.impactVisual.offScale")}
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
                    {t("devices.activityFeed.peakScale")}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                <SnippetSparkline
                  samples={samples}
                  w={340}
                  h={72}
                  interactive
                  dark={false}
                />
              </div>
              <div className="mt-0.5 flex justify-between font-mono text-[9px] text-[#B8B3AC]">
                <span>0 s</span>
                <span>
                  <span className="text-[#9CA3AF]">
                    {t("devices.impactVisual.gravity")}
                  </span>
                  {" · "}
                  <span className="text-[#E07A3A]">
                    {t("devices.impactVisual.trigger")}
                  </span>
                </span>
                <span>1.9 s</span>
              </div>
            </>
          )}
          {showMeter && (
            <>
              <div className="flex items-baseline justify-between">
                <span
                  className="font-mono text-[18px] font-bold tabular-nums leading-none"
                  style={{ color: sev.color }}
                >
                  {gStr(mg)}
                </span>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: sev.color }}
                >
                  {sev.label}
                  {ev.band && ev.band !== sev.level
                    ? t("devices.activityFeed.bandSuffix", { band: ev.band })
                    : ""}
                </span>
              </div>
              <div className="mt-1">
                <ImpactMeter peakMg={mg} w={340} h={16} dark={false} />
              </div>
              <div className="mt-0.5 flex justify-between font-mono text-[9px] text-[#B8B3AC]">
                <span>0 g</span>
                <span>{t("devices.impactVisual.severityScale")}</span>
                <span>{IMPACT_FS_G} g+</span>
              </div>
            </>
          )}
          {isImpact && (ev.rise_ms != null || ev.dur_ms != null) && (
            <div className="mt-2 border-t border-[#F0EEEA] pt-1.5">
              <ImpactPulse
                riseMs={ev.rise_ms}
                durMs={ev.dur_ms}
                color={sev.color}
                w={340}
                h={40}
                dark={false}
              />
            </div>
          )}
          {hasAxes && (
            <div className="mt-2 border-t border-[#F0EEEA] pt-1.5">
              <AxisBars comps={comps} w={280} h={42} labels dark={false} />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <dl>
            {rows.map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 py-[3px]"
              >
                <dt className="text-[11px] text-[#9CA3AF]">{k}</dt>
                <dd className="truncate text-right font-mono text-[11px] tabular-nums text-[#374151]">
                  {String(v)}
                </dd>
              </div>
            ))}
          </dl>
          <button
            onClick={copyJson}
            className={`mt-2 flex items-center gap-1.5 rounded-md border border-[#E5E7EB] bg-white px-2 py-1 text-[11px] font-medium text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151] ${FOCUS}`}
          >
            <Copy size={11} />
            {t("devices.impactVisual.copyJson")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Dry-run gate verdict chip (fw v0.6.2+).
//
// Deliberately NOT styled as an error. The firmware evaluates these gates and
// records the verdict; it never rejects, so a "too narrow" event was still
// detected, still broadcast and still stored. Amber says "look at this", which
// is the actual meaning -- red would say "this failed", which is wrong and
// would teach the wrong thing about what the row represents.
//
// PASS is left unchipped on purpose: chipping every passing row would bury the
// handful that did not, which are the only interesting ones.
const GateChip = ({ gate }) => {
  const { t } = useTranslation();
  if (!gate || gate === "PASS" || gate === "n/a" || gate === "?") return null;
  return (
    <span
      className="ml-2 inline-flex items-center rounded bg-[#FEF6EC] px-1 py-0.5 text-[10px] font-medium text-[#B45309]"
      title={t("devices.activityFeed.gateTitle", {
        verdict: gateVerdictLabel(gate),
      })}
    >
      {gateVerdictLabel(gate)}
    </span>
  );
};

// Flag chips on a grouped impact row: the broadcast and flash-save follow-ups
// that used to be their own rows.
const FlagChips = ({ flags }) => {
  const { t } = useTranslation();
  if (!flags) return null;
  const chips = [];
  if (flags.broadcast)
    chips.push({ label: t("devices.activityFeed.flagSent"), tone: "muted" });
  if (flags.retx)
    chips.push({
      label:
        flags.retx > 1
          ? t("devices.activityFeed.flagRetxCount", { count: flags.retx })
          : t("devices.activityFeed.flagRetx"),
      tone: "muted",
    });
  if (flags.saved)
    chips.push({ label: t("devices.activityFeed.flagSaved"), tone: "muted" });
  if (flags.saveFailed)
    chips.push({
      label: t("devices.activityFeed.flagSaveFailed"),
      tone: "error",
    });
  if (!chips.length) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-1">
      {chips.map((c) => (
        <span
          key={c.label}
          className={`rounded px-1 py-0.5 text-[10px] font-medium ${
            c.tone === "error"
              ? "bg-[#FEF2F2] text-[#DC2626]"
              : "bg-[#F5F4F1] text-[#9CA3AF]"
          }`}
        >
          {c.label}
        </span>
      ))}
    </span>
  );
};

/**
 * The structured activity feed: filter chips, grouped impact rows with
 * click-to-expand detail, and a teaching empty state. Pure presentation —
 * the rows come from useLiveFeed via the modal, so this can mount anywhere
 * (Live tab, under an active flow) without rescanning history.
 */
const LiveActivityFeed = ({ feed, connected, hideTitle = false }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const visible = useMemo(
    () => (filter === "all" ? feed : feed.filter((r) => r.group === filter)),
    [feed, filter]
  );

  return (
    <section>
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
        {!hideTitle && (
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold text-[#111827]">
              {t("devices.activityFeed.title")}
            </h3>
            {connected && (
              <span className="flex items-center gap-1 rounded bg-[#DCFCE7] px-1.5 py-0.5 text-[10px] font-medium text-[#16A34A]">
                <span className="relative flex h-1 w-1">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-75" />
                  <span className="relative inline-flex h-1 w-1 rounded-full bg-[#16A34A]" />
                </span>
                {t("common.status.live")}
              </span>
            )}
          </div>
        )}
        <div className={`flex items-center ${hideTitle ? "gap-2" : "gap-1"}`}>
          {FEED_FILTERS.map((f) => {
            const on = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`rounded-full border font-medium transition duration-150 ${FOCUS} ${
                  hideTitle
                    ? "px-3 py-1.5 text-[12px]"
                    : "px-2.5 py-1 text-[11px]"
                } ${
                  on
                    ? "border-[#E07A3A]/40 bg-[#FFF3ED] text-[#E07A3A]"
                    : hideTitle
                    ? "border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
                    : "border-transparent text-[#6B7280] hover:bg-[#F3F4F6]"
                }`}
              >
                {t(`devices.activityFeed.filters.${f.id}`, {
                  defaultValue: f.label,
                })}
              </button>
            );
          })}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#E3DFD9] bg-white/60 px-8 py-10 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F5F4F1]">
            <Activity size={20} className="text-[#9CA3AF]" />
          </div>
          <p className="text-sm font-semibold text-[#111827]">
            {feed.length === 0
              ? t("devices.activityFeed.emptyListening")
              : t("devices.activityFeed.emptyFiltered")}
          </p>
          <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-[#6B7280]">
            {feed.length === 0
              ? t("devices.activityFeed.emptyListeningBody")
              : t("devices.activityFeed.emptyFilteredBody")}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#F7F6F4] rounded-xl border border-[#EDEDEA] bg-white">
          {visible.map((row) => {
            const tone = TONE[row.tone] || TONE.default;
            const Icon = KIND_ICON[row.kind] || Activity;
            const muted = row.tone === "muted";
            const expandable =
              row.kind === "impact" ||
              row.kind === "broadcast" ||
              row.kind === "waveform";
            const open = expandable && expandedId === row.id;
            const toggle = () => setExpandedId(open ? null : row.id);
            return (
              <div key={row.id}>
                <div
                  role={expandable ? "button" : undefined}
                  tabIndex={expandable ? 0 : undefined}
                  aria-expanded={expandable ? open : undefined}
                  onClick={expandable ? toggle : undefined}
                  onKeyDown={
                    expandable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggle();
                          }
                        }
                      : undefined
                  }
                  className={`flex items-center gap-3 px-4 py-2 ${
                    expandable
                      ? `cursor-pointer transition duration-150 hover:bg-[#FAFAF9] ${FOCUS}`
                      : ""
                  }`}
                >
                  <span className="w-[56px] flex-shrink-0 font-mono text-[11px] tabular-nums text-[#B8B3AC]">
                    {timeOf(row.ts)}
                  </span>
                  <span
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: tone.bg, color: tone.fg }}
                  >
                    <Icon size={13} />
                  </span>
                  <div className="min-w-0 flex-1 truncate">
                    <span
                      className={`text-[13px] ${
                        muted
                          ? "font-normal text-[#6B7280]"
                          : "font-medium text-[#111827]"
                      }`}
                    >
                      {row.title}
                    </span>
                    {row.detail && (
                      <span className="ml-2 text-xs text-[#9CA3AF]">
                        {row.detail}
                      </span>
                    )}
                    <GateChip gate={row.gate} />
                    <FlagChips flags={row.flags} />
                  </div>
                  {row.value && (
                    <span
                      className={`flex-shrink-0 font-mono text-[13px] font-semibold tabular-nums ${
                        row.tone === "accent"
                          ? "text-[#E07A3A]"
                          : "text-[#6B7280]"
                      }`}
                    >
                      {row.value}
                    </span>
                  )}
                  {expandable && (
                    <ChevronRight
                      size={14}
                      className={`flex-shrink-0 text-[#C9C4BD] transition-transform duration-150 ${
                        open ? "rotate-90" : ""
                      }`}
                    />
                  )}
                </div>
                {open && <ImpactDetail ev={row.ev} txEv={row.txEv} />}
              </div>
            );
          })}
          {feed.length >= FEED_CAP && (
            <div className="px-4 py-2 text-center text-[11px] text-[#B8B3AC]">
              {t("devices.activityFeed.showingLast", { cap: FEED_CAP })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default LiveActivityFeed;
